import React from 'react';
import styled from 'styled-components';

import { HorizontalTabs, HorizontalTab } from 'components/core';
import useTranslation from 'hooks/use-translation';
import { ARIA_ROLES } from 'shared/constants';
import { TabLink } from 'src/components/core/tabs/tab-link';
import { RouteParam } from 'src/types';

import { AccountRoutes, AccountTab } from '../account.types';

type AccountTopNavBarProps = {
  accountRoutes: AccountRoutes;
  selectedTab: RouteParam;
  shouldDisplayDutchiePaySection: boolean;
  shouldDisplayLoyaltySection: boolean;
  shouldDisplayReferralsSection: boolean;
};

const ANCHOR_COMPONENT = `a`;

export function AccountTopNavBar(props: AccountTopNavBarProps): JSX.Element {
  const {
    accountRoutes,
    selectedTab,
    shouldDisplayDutchiePaySection,
    shouldDisplayLoyaltySection,
    shouldDisplayReferralsSection,
  } = props;
  const { account, preferences, orders, bankAccount, loyalty, referrals } = accountRoutes;
  const { t } = useTranslation();

  return (
    <TopNavBarContainer>
      <HorizontalTabs
        aria-label={t('accountPage.navigationTabs', 'My account page navigation tabs')}
        scrollButtons='off'
        selectionFollowsFocus
        value={selectedTab}
        variant='scrollable'
      >
        {shouldDisplayDutchiePaySection && bankAccount && (
          <TabLink value={AccountTab.bankAccount} href={bankAccount}>
            <HorizontalTab
              id='account-tab-bank-account'
              data-testid='account-tab-bank-account'
              aria-controls='account-tab-panel-dutchie-pay'
              aria-selected={selectedTab === AccountTab.bankAccount}
              component={ANCHOR_COMPONENT}
              label={t('accountPage.bankAccount', 'Bank Account')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.bankAccount}
            />
          </TabLink>
        )}
        <TabLink value={AccountTab.account} href={account}>
          <HorizontalTab
            id='account-tab-my-account'
            data-testid='account-tab-my-account'
            aria-controls='account-tab-panel-my-account'
            aria-selected={selectedTab === AccountTab.account}
            component={ANCHOR_COMPONENT}
            label={t('accountPage.accountDetails', 'Account Details')}
            role={ARIA_ROLES.TAB}
            value={AccountTab.account}
          />
        </TabLink>
        <TabLink value={AccountTab.orders} href={orders}>
          <HorizontalTab
            id='account-tab-orders'
            data-testid='account-tab-orders'
            aria-controls='account-tab-panel-orders'
            aria-selected={selectedTab === AccountTab.orders}
            component={ANCHOR_COMPONENT}
            label={t('accountPage.orders', 'Orders')}
            role={ARIA_ROLES.TAB}
            value={AccountTab.orders}
          />
        </TabLink>

        {shouldDisplayReferralsSection && referrals && (
          <TabLink value={AccountTab.referrals} href={referrals}>
            <HorizontalTab
              id='account-tab-referrals'
              data-testid='account-tab-referrals'
              aria-controls='account-tab-panel-referrals'
              aria-selected={selectedTab === AccountTab.referrals}
              component={ANCHOR_COMPONENT}
              label={t('accountPage.referrals', 'Referrals')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.referrals}
            />
          </TabLink>
        )}

        {shouldDisplayLoyaltySection && loyalty && (
          <TabLink value={AccountTab.loyalty} href={loyalty}>
            <HorizontalTab
              id='account-tab-loyalty'
              data-testid='account-tab-loyalty'
              aria-controls='account-tab-panel-loyalty'
              aria-selected={selectedTab === AccountTab.loyalty}
              component={ANCHOR_COMPONENT}
              label={t('accountPage.loyalty', 'Loyalty')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.loyalty}
            />
          </TabLink>
        )}

        <TabLink value={AccountTab.preferences} href={preferences}>
          <HorizontalTab
            id='account-tab-preferences'
            data-testid='account-tab-preferences'
            aria-controls='account-tab-panel-preferences'
            aria-selected={selectedTab === AccountTab.preferences}
            component={ANCHOR_COMPONENT}
            label={t('accountPage.preferences', 'Preferences')}
            role={ARIA_ROLES.TAB}
            value={AccountTab.preferences}
          />
        </TabLink>
      </HorizontalTabs>
    </TopNavBarContainer>
  );
}

const TopNavBarContainer = styled.div`
  color: var(--dcm-account-nav-color);
`;
